import React from 'react';
import { LicensePlate } from '../types';
import RatingButtons from './RatingButtons';
import EmojiReactions from './EmojiReactions';
import { useRouter } from '../context/RouterContext';

interface PlateCardProps {
  plate: LicensePlate;
  onVote: (plateId: string, voteType: 'upvote' | 'downvote') => void;
  hasVoted: boolean;
  onEmojiReaction: (plateId: string, emoji: string) => void;
}

const PlateCard: React.FC<PlateCardProps> = ({ plate, onVote, hasVoted, onEmojiReaction }) => {
  const { navigate } = useRouter();
  
  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleImageClick = () => {
    navigate('plate-detail', plate.id);
  };

  return (
    <article className="bg-white dark:bg-slate-800/50 rounded-2xl border border-gray-200/60 dark:border-slate-700/50 overflow-hidden shadow-soft dark:shadow-soft-dark transition-all duration-300 hover:shadow-lg dark:hover:shadow-soft-dark animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <span className="text-white text-xs font-bold">R8</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-gray-900 dark:text-white text-sm">ratemyplate</span>
          {plate.state && (
            <span className="ml-2 text-xs text-gray-500 dark:text-slate-400">• {plate.state}</span>
          )}
        </div>
        <button
          onClick={handleImageClick}
          className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
          aria-label="View details"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </button>
      </div>

      {/* Image */}
      <div
        className="aspect-square bg-gray-100 dark:bg-slate-700/30 relative cursor-pointer group overflow-hidden"
        onClick={handleImageClick}
      >
        <img
          src={plate.imageUrl}
          alt={`License plate from ${plate.state || 'unknown'} - ${plate.id}`}
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
          loading="lazy"
        />
      </div>

      {/* Action bar */}
      <div className="px-3 py-2 space-y-2">
        <div className="flex items-center gap-3">
          <RatingButtons
            plateId={plate.id}
            votes={plate.votes}
            onVote={onVote}
            hasVoted={hasVoted}
            compact
          />
          <button
            onClick={handleImageClick}
            className="text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white p-1 -ml-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
            aria-label="View full plate"
            title="View full plate"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        <div data-emoji-reaction>
          <EmojiReactions
            reactions={plate.emojiReactions || []}
            onReaction={(emoji) => onEmojiReaction(plate.id, emoji)}
          />
        </div>

        <div className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">
          {plate.votes.upvotes + plate.votes.downvotes > 0 && (
            <span className="font-semibold text-gray-900 dark:text-white">{plate.votes.upvotes + plate.votes.downvotes} votes</span>
          )}
          <span className="text-gray-400 dark:text-slate-500 text-xs">{formatDate(plate.uploadDate)}</span>
        </div>
      </div>
    </article>
  );
};

export default PlateCard;
